/**
 * THE MODIFIER STEPPER'S MEMORY, and the one place the
 * line's form is submitted from (CAL-509).
 *
 * A press on a modifier stepper cannot be its own
 * request. Four presses in a second is four PATCHes at
 * the same line, each rewriting it from the state the
 * browser held when it left, and the last answer to
 * land wins: the merchant watched a count go 4, 1, 3,
 * and then revert. So a burst is DEBOUNCED into one
 * request per line.
 *
 * A debounce that swallows presses is the same bug
 * pointed the other way, and the thing that makes it
 * hard is that the response REPLACES the lines card:
 * the element the merchant is pressing is destroyed and
 * re-rendered from server state, mid-burst, several
 * times a second. Anything the controller remembers in
 * an instance field dies with it.
 *
 * So the pending count lives HERE, outside the DOM,
 * keyed by `<lineId>:<modifierId>`. A fresh controller
 * asks on `connect` whether it is holding a value the
 * server has not caught up with, renders THAT rather
 * than what the server just sent, and schedules the
 * submit that will settle it.
 *
 * `submitted` is what stops that becoming a loop. An
 * entry the server has already been TOLD about and
 * answered differently is an entry the server refused
 * (a full group, a shelf that emptied, a `max` we do
 * not know about); truth wins and the entry is dropped.
 * An entry that has never been sent is a press the
 * merchant made while a request was in flight, and it
 * is applied. The two are indistinguishable from the
 * count alone, which is why the flag exists.
 *
 * The line id is STABLE across a modifier edit, which
 * is what makes the key work at all.
 */
const pending = new Map();
const timers = new Map();

// Long enough that a thumb tapping "add four" spends
// one request, short enough that the count under it is
// not visibly ahead of the total in the other column.
export const SUBMIT_DELAY_MS = 350;

export function keyFor(lineId, modifierId) {
  return `${lineId}:${modifierId}`;
}

export function pendingFor(key) {
  return pending.get(key);
}

export function setPending(key, value) {
  // A NEW value has not been submitted, whatever the
  // entry it replaces had been through. Carrying the
  // flag over would let one in-flight request answer
  // for a press it never carried.
  pending.set(key, { value, submitted: false });
}

export function clearPending(key) {
  pending.delete(key);
}

export function scheduleSubmit(formId, lineId) {
  // ONE TIMER PER FORM, not per control. Two steppers
  // on one line share a form, so two timers would be
  // two requests carrying identical bodies.
  clearTimeout(timers.get(formId));

  timers.set(
    formId,
    setTimeout(() => {
      timers.delete(formId);

      // Nothing unsent left means the server already
      // agrees, and a submit here would be a round trip
      // that changes nothing. It happens: a press back
      // to the value the server just confirmed.
      if (!hasUnsent(lineId)) return;

      submitLine(formId, lineId);
    }, SUBMIT_DELAY_MS)
  );
}

/**
 * Every pending count on this line rides in this one
 * body, so every one of them is marked before it goes.
 * A pill submitting for its own reasons carries them
 * too, which is why this is the only door out.
 */
export function submitLine(formId, lineId) {
  const form = document.getElementById(formId);

  // THE FORM IS GONE, SO THE PRESS IS ABANDONED, and the
  // entry must go with it. The line id is stable across a
  // modifier edit, so this form keeps its id through every
  // frame replacement: missing means the merchant LEFT the
  // builder inside the debounce window, on a journey the
  // staff cart is built to survive (it is one basket per
  // employee, kept across a reload, a section switch and a
  // trip to the orders list).
  //
  // Left behind, the entry stays `submitted: false`
  // forever, which `connect()` reads as "a press made
  // while a request was in flight" and COMMITS. So
  // reopening the builder silently re-applied a modifier
  // the merchant had walked away from, to an order they
  // had only just opened (CAL-509).
  if (!form) {
    clearLine(lineId);
    return;
  }

  markSubmitted(lineId);
  form.requestSubmit();
}

function clearLine(lineId) {
  const prefix = `${lineId}:`;

  Array.from(pending.keys())
    .filter((key) => key.startsWith(prefix))
    .forEach((key) => pending.delete(key));
}

function markSubmitted(lineId) {
  const prefix = `${lineId}:`;

  pending.forEach((entry, key) => {
    if (key.startsWith(prefix)) entry.submitted = true;
  });
}

function hasUnsent(lineId) {
  const prefix = `${lineId}:`;

  return Array.from(pending.entries()).some(
    ([key, entry]) => key.startsWith(prefix) && !entry.submitted
  );
}

/**
 * Module state outlives a jsdom document, so the specs
 * need a way back to an empty page. Nothing in the app
 * calls this.
 */
export function resetPendingQuantities() {
  timers.forEach((timer) => clearTimeout(timer));
  timers.clear();
  pending.clear();
}
